import { RESEND_COOLDOWN_SECONDS } from "@/constants/smsAuthCode";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import { smsPhoneNumberSchema } from "@/validations/phoneNumber";

export const SEND_AUTH_CODE_FAILED_MESSAGE = "電話番号認証コード送信に失敗しました。";
export const SEND_AUTH_CODE_UNAVAILABLE_MESSAGE =
  "認証コードの送信を確認できませんでした。時間をおいて、もう一度お試しください。";
export const SEND_AUTH_CODE_LIMIT_MESSAGE =
  "認証コードの送信回数の上限に達しました。しばらく時間をおいて、もう一度お試しください。";
export const EXISTING_AUTH_CODE_MESSAGE = "届いている認証コードをそのまま使えます。";

export interface SendPhoneNumberAuthCodeResult {
  success: boolean;
  error: string;
  kind: "info" | "error";
  // 成功時の再送間隔、または429の残り秒数。旧APIには60秒で対応する。
  retryAfter?: number;
  codeAvailable?: boolean;
  deliveryState?: "pending" | "sent";
}

const parseRetryAfter = (value: unknown): number => {
  if (typeof value !== "number" && typeof value !== "string") return RESEND_COOLDOWN_SECONDS;
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds <= 0) return RESEND_COOLDOWN_SECONDS;
  return Math.ceil(seconds);
};

// 登録と設定の電話番号認証で共用する。画面遷移は呼び出し側が判断する。
export const sendPhoneNumberAuthCode = async (phoneNumber: string): Promise<SendPhoneNumberAuthCodeResult> => {
  const validation = smsPhoneNumberSchema.safeParse(phoneNumber);
  if (!validation.success) {
    return { success: false, error: validation.error.issues[0].message, kind: "error" };
  }
  const fp = PropallyApiFactory();
  try {
    const response = await fp.v1SendPhoneNumberAuthCodesPost({ phone_number: phoneNumber }, { withCredentials: true });
    if (response.status >= 200 && response.status < 300) {
      const data = response.data as { retry_after?: unknown } | undefined;
      return { success: true, error: "", kind: "info", retryAfter: parseRetryAfter(data?.retry_after) };
    }
    return { success: false, error: SEND_AUTH_CODE_FAILED_MESSAGE, kind: "error" };
  } catch (e) {
    const response = (e as {
      response?: {
        status?: number;
        data?: { code?: unknown; retry_after?: unknown; code_available?: unknown; delivery_state?: unknown };
      };
    } | null)?.response;
    if (response?.status === 429) {
      const data = response.data;
      if (data?.code === "SMS_SEND_RATE_LIMITED") {
        return { success: false, error: SEND_AUTH_CODE_LIMIT_MESSAGE, kind: "error", retryAfter: RESEND_COOLDOWN_SECONDS };
      }
      const retryAfter = parseRetryAfter(data?.retry_after);
      const knownRateLimit = data?.code === "SMS_AUTH_CODE_RATE_LIMITED";
      const deliveryState = knownRateLimit && (data?.delivery_state === "pending" || data?.delivery_state === "sent")
        ? data.delivery_state : undefined;
      const codeAvailable = knownRateLimit && data?.code_available === true && deliveryState !== "pending";
      const error = deliveryState === "pending"
        ? "認証コードの送信処理中です。しばらく待ってから、もう一度お試しください。"
        : codeAvailable
          ? `${EXISTING_AUTH_CODE_MESSAGE}再送は${retryAfter}秒後にできます。`
          : `現在の画面では認証コードを利用できません。${retryAfter}秒後にもう一度送信してください。`;
      const kind = codeAvailable || deliveryState === "pending" ? "info" : "error";
      return { success: false, error, kind, retryAfter, codeAvailable, deliveryState };
    }
    if (response?.status === 503) return { success: false, error: SEND_AUTH_CODE_UNAVAILABLE_MESSAGE, kind: "error" };
    return { success: false, error: SEND_AUTH_CODE_FAILED_MESSAGE, kind: "error" };
  }
};
